// The chat box: fading lines over the world while closed (outlined, on a dark band); Enter opens it framed, with the
// channel tabs (the sticky channel you speak in: typing "/s ", "/map ", "/g " or "/p " switches it at once), the input
// line, sent-message history, clickable names and emoji (":)" and friends, see ui/emoji.ts). The cog by the friends
// button sets its text size, the band's darkness, speech bubbles and the profanity filter (saved settings).
import { api, content } from '../api';
import { Widget, TextInput, EmojiIcon, focus, type UiPointerEvent, widget, textWidth, textPixels, uiScale, scaleRevision } from '../ui/compose';
import { splitEmoji } from '../ui/emoji';
import { settings, CHAT_TEXT_SIZES, CHAT_BACKGROUNDS } from '../settings';

interface ChatLine { t: number; color: string; label: string; name: string; after: string; text: string; spoken: string }

const FADE_AFTER = 12, FADE_DURATION = 2, VISIBLE = 9;
const CHANNELS: Record<string, string> = { s: 'Say', say: 'Say', map: 'Map', m: 'Map', g: 'Global', global: 'Global', p: 'Party', party: 'Party' };
const COMMAND: Record<string, string> = { Say: 's', Map: 'map', Global: 'g', Party: 'p' };
const COLOR: Record<string, string> = { Say: '#ece2d6', Map: '#a0e196', Global: '#f0c8aa', Party: '#78d2f0' };
/** The chat's width at normal text size (larger chat text widens it); a line's text gets this less the chat-box
 *  padding (2 x 4), chat-line padding (2 x 3) and a pixel spare. */
const WIDTH = 260, LINE_SPARE = 15, LOG_HEIGHT = 130;
/** Seconds a speech bubble shows: a base, more for longer messages, at most the max; the last BUBBLE_FADE fades. */
const BUBBLE_MIN = 4, BUBBLE_PER_CHAR = 0.06, BUBBLE_MAX = 10, BUBBLE_FADE = 0.5;

type Piece = { text: string; name?: string } | { emoji: string };

export class Chat {
  readonly root: Widget;
  private readonly log: Widget;
  private readonly field: TextInput;
  private readonly channelLabel: Widget;
  private readonly inputRow: Widget;
  private readonly tabs: Widget;
  private lines: ChatLine[] = [];
  private shownRev = -1;
  private channel = 'Say';
  private readonly history: string[] = [];
  private historyIndex = -1;
  private scroll = 0;
  /** The last thing each player said aloud (Say, Party, Map), by name, for the bubble over their head. */
  readonly said = new Map<string, ChatLine>();
  /** Built line widgets by content, so a new message lays out only itself. */
  private built = new Map<string, Widget>();
  /** The chat settings the built lines were laid out for. */
  private look = '';
  private lineWidth = WIDTH - LINE_SPARE;
  open = false;

  /** Right-click on a name in the log. */
  onName: (name: string, x: number, y: number) => void = () => { };
  onIgnoreList: () => void = () => { };
  onFriendList: () => void = () => { };
  /** The cog: the chat settings menu at a screen point. */
  onSettings: (x: number, y: number) => void = () => { };

  constructor() {
    this.log = widget('box', { class: 'chat-log' });
    this.field = widget('input', { class: 'field chat-field', testId: 'chat-entry', maxLength: content.maxChat + 32, spellcheck: false, autocomplete: 'off' });
    this.channelLabel = widget('box', { class: 'chat-channel shadowed' });
    const friends = widget('button', { class: 'button chat-button', title: 'Friends (O)', onclick: () => this.onFriendList() },
      widget('box', { class: 'sprite chat-icon icon-friends' }));
    const cog = widget('button', { class: 'button chat-button', title: 'Chat settings', testId: 'chat-settings',
      onclick: (e: UiPointerEvent) => this.onSettings(e.clientX, e.clientY) }, widget('box', { class: 'sprite chat-icon icon-settings' }));
    const ignore = widget('button', { class: 'button chat-button', title: 'Ignore list', onclick: () => this.onIgnoreList() },
      widget('box', { class: 'sprite chat-icon icon-ignore' }));
    this.inputRow = widget('box', { class: 'chat-input' }, this.channelLabel, this.field);
    this.tabs = widget('box', { class: 'chat-tabs' });
    for (const channel of Object.keys(COMMAND))
      this.tabs.append(widget('text', { class: 'chat-tab', onmousedown: (e: UiPointerEvent) => { e.preventDefault(); this.channel = channel; this.field.focus(); } }, channel));
    this.tabs.append(widget('text', { class: 'grow' }), cog, friends, ignore);
    this.root = widget('box', { class: 'chat' }, widget('box', { class: 'panel chat-box' }, this.tabs, this.log, this.inputRow));
    // The touch keyboard covers the log, so it shows the latest lines itself.
    this.field.keyboardContext = () => this.lines.slice(-4).map(l => ({ text: l.label + l.name + l.after + l.text, color: l.color }));
    this.field.addEventListener('keydown', e => this.onKey(e));
    this.field.addEventListener('input', () => this.switchOnSpace());
    this.setOpen(false);
  }

  private onKey(e: KeyboardEvent): void {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const text = this.field.value.trim();
      if (text) {
        const send = this.outgoing(text);
        if (send) api.sendChat(send);
        this.history.push(text);
        if (this.history.length > 50) this.history.shift();
      }
      this.close();
      e.preventDefault();
    } else if (e.key === 'Escape') {
      this.close();
    } else if (e.key === 'ArrowUp' && this.history.length) {
      this.historyIndex = Math.min(this.history.length - 1, this.historyIndex + 1);
      this.field.value = this.history[this.history.length - 1 - this.historyIndex];
      e.preventDefault();
    } else if (e.key === 'ArrowDown' && this.historyIndex >= 0) {
      this.historyIndex--;
      this.field.value = this.historyIndex < 0 ? '' : this.history[this.history.length - 1 - this.historyIndex];
      e.preventDefault();
    } else if (e.key === 'PageUp') {
      this.scroll = Math.min(Math.max(0, this.lines.length - VISIBLE), this.scroll + VISIBLE - 1);
      this.shownRev = -1;
    } else if (e.key === 'PageDown') {
      this.scroll = Math.max(0, this.scroll - (VISIBLE - 1));
      this.shownRev = -1;
    }
  }

  /** "/g " (a channel command and its space) switches channel at once, leaving the box empty for the message. */
  private switchOnSpace(): void {
    const command = /^\/(\S+) $/.exec(this.field.value)?.[1];
    const channel = command && CHANNELS[command.toLowerCase()];
    if (!channel) return;
    this.channel = channel;
    this.field.value = '';
  }

  /** What to send for a typed line (null: it only switched channel). */
  private outgoing(text: string): string | null {
    if (!text.startsWith('/')) return `/${COMMAND[this.channel]} ${text}`;
    const [cmd, ...rest] = text.slice(1).split(' ').filter(p => p.length);
    const channel = CHANNELS[(cmd ?? '').toLowerCase()];
    if (channel) {
      this.channel = channel;
      return rest.length ? text : null;
    }
    return text;
  }

  openWith(prefix = ''): void {
    this.setOpen(true);
    this.field.value = prefix;
    this.historyIndex = -1;
    this.scroll = 0;
    this.field.focus();
  }

  close(): void {
    this.setOpen(false);
    this.field.value = '';
    this.field.blur();
    this.scroll = 0;
  }

  private setOpen(open: boolean): void {
    this.open = open;
    this.root.classList.toggle('open', open);
    this.shownRev = -1;
  }

  /** Rebuilds the log when the session changed; fades lines by age. */
  update(rev: number, now: number): void {
    const factor = CHAT_TEXT_SIZES[settings.chatTextSize].factor;
    const look = [factor, settings.chatBackground, settings.profanityFilter, scaleRevision].join('|');
    if (look !== this.look) {
      this.look = look;
      const width = Math.round(WIDTH * Math.max(1, factor));
      this.lineWidth = width - LINE_SPARE;
      this.root.style.width = `${width}px`;
      this.log.style.fontFactor = String(factor);
      this.log.style.maxHeight = `${Math.round(LOG_HEIGHT * Math.max(1, factor))}px`;
      this.shownRev = -1;
    }
    this.channelLabel.textContent = `${this.channel}:`;
    this.field.label = `Chat (${this.channel})`;
    this.channelLabel.style.color = this.field.style.color = COLOR[this.channel];
    for (const tab of Array.from(this.tabs.querySelectorAll('.chat-tab'))) tab.classList.toggle('active', tab.textContent === this.channel);
    if (rev !== this.shownRev) {
      this.shownRev = rev;
      this.lines = api.chat(settings.profanityFilter) as ChatLine[];
      this.said.clear();
      for (const l of this.lines) if (l.spoken) this.said.set(l.spoken, l);
      const shown = this.lines.slice(this.open ? 0 : Math.max(0, this.lines.length - VISIBLE - this.scroll), this.lines.length - this.scroll);
      const built = new Map<string, Widget>();
      this.log.replaceChildren(...shown.map(l => {
        let key = [l.t, l.color, l.label, l.name, l.after, l.text, this.open, look].join('\u0000');
        while (built.has(key)) key += '+';
        const el = this.built.get(key) ?? this.lineWidget(l);
        built.set(key, el);
        return el;
      }));
      this.built = built;
      this.log.scrollTop = Number.MAX_SAFE_INTEGER;
      if (this.lines.length === 0 && !this.open) this.log.append(widget('box', { class: 'chat-line dim', style: { opacity: '0.6', ...this.band() } }, 'Press Enter to chat'));
    }
    for (const el of Array.from(this.log.widgets) as Widget[]) {
      const t = (el as any).t as number | undefined;
      if (t === undefined) continue;
      const age = now - t;
      el.style.opacity = this.open ? '1' : String(Math.max(0, Math.min(1, 1 - (age - FADE_AFTER) / FADE_DURATION)));
    }
  }

  /** Over the world a line is outlined, on a band as dark as the settings say. */
  private band(): { textOutline: string; background?: string } {
    const alpha = CHAT_BACKGROUNDS[settings.chatBackground].alpha;
    return alpha ? { textOutline: '1', background: `rgba(8,6,8,${alpha})` } : { textOutline: '1' };
  }

  /** The bubble over a player who spoke lately: shown a while by length, then faded out. */
  bubble(name: string, now: number): { text: string; color: string; alpha: number; scale: number } | undefined {
    const l = settings.chatBubbles ? this.said.get(name) : undefined;
    if (!l) return undefined;
    const left = Math.min(BUBBLE_MAX, BUBBLE_MIN + l.text.length * BUBBLE_PER_CHAR) - (now - l.t);
    return left > 0 ? { text: l.text, color: l.color, alpha: Math.min(1, left / BUBBLE_FADE), scale: CHAT_TEXT_SIZES[settings.chatTextSize].factor } : undefined;
  }

  /** One message, wrapped into rows by word (names and emoji never split); over the world it is outlined on a band. */
  private lineWidget(l: ChatLine): Widget {
    const outline = !this.open;
    const pieces: Piece[] = [{ text: l.label }, ...(l.name ? [{ text: l.name, name: l.name }] : []), { text: l.after },
      ...splitEmoji(l.text).map(p => typeof p === 'string' ? { text: p } : p)];
    const factor = CHAT_TEXT_SIZES[settings.chatTextSize].factor, lineWidth = this.lineWidth;
    const emojiSize = Math.round(textPixels() * factor * 1.25 * uiScale) / uiScale;
    const width = (p: { text: string; name?: string }, text = p.text) => textWidth(text, p.name ? '600' : '400', outline, factor);
    // A label's width includes its outline and shadow; pieces overlap by that much so a row reads as one text.
    const overlap = (outline ? 4 : 1) / uiScale;
    const rows: Piece[][] = [[]];
    let x = 0;
    const newRow = () => { if (rows[rows.length - 1].length) rows.push([]); x = 0; };
    const add = (p: Piece, w: number) => { rows[rows.length - 1].push(p); x += w - overlap; };
    for (const p of pieces) {
      if ('emoji' in p) {
        if (x + emojiSize > lineWidth) newRow();
        add(p, emojiSize + overlap);
      } else if (p.name) {
        if (x + width(p) > lineWidth) newRow();
        add(p, width(p));
      } else {
        let run = '';
        const flush = () => { if (run) add({ text: run }, width(p, run)); run = ''; };
        for (let token of p.text.match(/\S+\s*|\s+/g) ?? []) {
          if (x + width(p, (run + token).trimEnd()) <= lineWidth) { run += token; continue; }
          flush(); newRow();
          token = token.trimStart();
          if (width(p, token.trimEnd()) <= lineWidth) { run = token; continue; }
          for (const ch of token) { // a word wider than the whole line breaks anywhere
            if (run && width(p, run + ch) > lineWidth) { flush(); newRow(); }
            run += ch;
          }
        }
        flush();
      }
    }
    const line = widget('box', { class: 'chat-line', style: { color: l.color, ...(outline ? this.band() : {}) } });
    for (const row of rows) {
      line.append(widget('box', { class: 'chat-row', style: { gap: `${-overlap}px` } }, ...row.map((p, i) => {
        if ('emoji' in p) return widget('box', { style: { padding: `0 ${overlap / 2}px` } }, new EmojiIcon(p.emoji, emojiSize));
        const name = p.name;
        if (name) return widget('text', { class: 'chat-name', oncontextmenu: (e: UiPointerEvent) => { e.preventDefault(); this.onName(name, e.clientX, e.clientY); } }, name);
        let text = p.text;
        if (i === 0) text = text.trimStart();
        if (i === row.length - 1) text = text.trimEnd();
        return text;
      })));
    }
    (line as any).t = l.t;
    return line;
  }
}
